// Login client for the API's /auth endpoints (replaces aws-amplify/auth).
//
// The exported functions keep the names and result shapes of the
// aws-amplify/auth v6 calls they replace (signIn -> { isSignedIn, nextStep },
// errors carry Cognito's exception names in `error.name`, Hub 'auth' events
// 'signedIn' / 'signedOut', useAuthenticator() -> { user, route, signOut }),
// so the pages only had to change their imports.
//
// Tokens are kept in localStorage (as Amplify did), so a sign-in survives
// reloads and new tabs. The access token is a 60 min JWT sent as
// "Authorization: Bearer"; the refresh token is rotated on every refresh.
import axios from "axios";
import mitt from "mitt";
import { reactive } from "vue";

export const PATIENT_POOL = "PATIENT"; // username: E.164 phone number
export const DOCTOR_POOL = "DOCTOR"; // username: email (doctor, clinic, referee, admin)

const SESSION_KEY = "yourdoc.auth.session";
const POOL_KEY = "yourdoc.auth.pool";
// Refresh this long before the access token actually expires.
const EXPIRY_MARGIN_MS = 60 * 1000;

// ------------------------------------------------------------------ storage

function storageGet(storage, key) {
  try {
    return storage.getItem(key);
  } catch (e) {
    return null;
  }
}

function storageSet(storage, key, value) {
  try {
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, value);
  } catch (e) {
    // Private mode / blocked storage: the session just won't persist.
  }
}

function readSession() {
  try {
    return JSON.parse(storageGet(localStorage, SESSION_KEY)) || null;
  } catch (e) {
    return null;
  }
}

// ------------------------------------------------------------------ Hub + useAuthenticator

const emitter = mitt();

/** Stand-in for Amplify's Hub: Hub.listen("auth", ({ payload }) => payload.event). */
export const Hub = {
  listen(channel, callback) {
    emitter.on(channel, callback);
    return () => emitter.off(channel, callback);
  },
  dispatch(channel, payload) {
    emitter.emit(channel, { channel, payload });
  },
};

const authenticator = reactive({
  user: null,
  route: "signIn",
  signOut: () => signOut(),
});

/** Stand-in for @aws-amplify/ui-vue's useAuthenticator(): reactive { user, route, signOut }. */
export function useAuthenticator() {
  return authenticator;
}

function toCurrentUser(user) {
  return user ? { username: user.username, userId: user.userId, signInDetails: { loginId: user.username } } : null;
}

function writeSession(session) {
  storageSet(localStorage, SESSION_KEY, session ? JSON.stringify(session) : null);
  authenticator.user = toCurrentUser(session && session.user);
  authenticator.route = session ? "authenticated" : "signIn";
  if (session) axios.defaults.headers.common["Authorization"] = `Bearer ${session.accessToken}`;
  else delete axios.defaults.headers.common["Authorization"];
}

function sessionFromTokens(data) {
  return {
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    expiresAt: Date.now() + data.expiresIn * 1000,
    user: data.user,
  };
}

// ------------------------------------------------------------------ pools

/** Which login group the next call uses (was Amplify.configure(DOCTOR_POOL | PATIENT_POOL)). */
export function setUserPool(pool) {
  storageSet(sessionStorage, POOL_KEY, pool === DOCTOR_POOL ? DOCTOR_POOL : PATIENT_POOL);
}

export function getUserPool() {
  return storageGet(sessionStorage, POOL_KEY) === DOCTOR_POOL ? DOCTOR_POOL : PATIENT_POOL;
}

// ------------------------------------------------------------------ requests

export class AuthError extends Error {
  constructor(name, message) {
    super(message);
    this.name = name;
  }
}

function toAuthError(error) {
  const detail = error && error.response && error.response.data && error.response.data.detail;
  if (detail && detail.code) return new AuthError(detail.code, detail.message);
  if (error && error.response && error.response.status === 422)
    return new AuthError("InvalidParameterException", "Please check the details you entered.");
  if (typeof detail === "string") return new AuthError("AuthError", detail);
  if (error && !error.response)
    return new AuthError("NetworkError", "Could not reach the server. Please check your connection and try again.");
  return new AuthError("AuthError", "Something went wrong. Please try again.");
}

async function post(path, body) {
  try {
    const { data } = await axios.post(`/auth/${path}`, body);
    return data;
  } catch (error) {
    throw toAuthError(error);
  }
}

function userUnauthenticated() {
  return new AuthError("UserUnAuthenticatedException", "User needs to be authenticated to call this API.");
}

// ------------------------------------------------------------------ session

let refreshInFlight = null;

async function refreshSession() {
  const run = async () => {
    // Another tab may have refreshed already - its token replaces ours, and
    // re-using our (now revoked) refresh token would sign everyone out.
    const current = readSession();
    if (!current) return null;
    if (current.expiresAt - EXPIRY_MARGIN_MS > Date.now()) {
      writeSession(current);
      return current;
    }
    try {
      const data = await post("refresh", { refreshToken: current.refreshToken });
      const next = sessionFromTokens(data);
      writeSession(next);
      return next;
    } catch (error) {
      if (error.name === "NotAuthorizedException") {
        writeSession(null);
        return null;
      }
      throw error; // offline etc. - keep the session for a later retry
    }
  };
  if (!refreshInFlight) {
    const locks = typeof navigator !== "undefined" && navigator.locks;
    refreshInFlight = (locks ? locks.request("yourdoc-auth-refresh", run) : run()).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Like Amplify: { tokens: { accessToken } , userSub } or { tokens: undefined } when signed out. */
export async function fetchAuthSession(options = {}) {
  let session = readSession();
  if (session && (options.forceRefresh || session.expiresAt - EXPIRY_MARGIN_MS <= Date.now())) {
    try {
      session = await refreshSession();
    } catch (error) {
      // Server unreachable: report the stored session; the next call retries.
    }
  }
  if (!session) return { tokens: undefined, userSub: undefined };
  return {
    tokens: { accessToken: session.accessToken },
    userSub: session.user.userId,
  };
}

export async function getCurrentUser() {
  const session = readSession();
  if (!session) throw userUnauthenticated();
  return toCurrentUser(session.user);
}

/** Old Amplify v5 name still imported by a header. */
export const currentAuthenticatedUser = getCurrentUser;

export async function fetchUserAttributes() {
  const session = readSession();
  if (!session) throw userUnauthenticated();
  const user = session.user;
  return {
    sub: user.userId,
    email: user.email || undefined,
    phone_number: user.phone_number || undefined,
    given_name: user.given_name || undefined,
    family_name: user.family_name || undefined,
  };
}

// ------------------------------------------------------------------ sign in / out

export async function signIn({ username, password, options = {} }) {
  if (!password) throw new AuthError("EmptySignInPassword", "password is required to signIn");
  let data;
  try {
    data = await post("signin", { pool: options.pool || getUserPool(), username, password });
  } catch (error) {
    if (error.name === "UserNotConfirmedException")
      return { isSignedIn: false, nextStep: { signInStep: "CONFIRM_SIGN_UP" } };
    throw error;
  }
  writeSession(sessionFromTokens(data));
  Hub.dispatch("auth", { event: "signedIn", data: toCurrentUser(data.user) });
  return { isSignedIn: true, nextStep: { signInStep: "DONE" } };
}

export async function signOut() {
  const session = readSession();
  writeSession(null);
  if (session) {
    try {
      await post("signout", { refreshToken: session.refreshToken });
    } catch (error) {
      // Signed out locally regardless; the refresh token just expires.
    }
  }
  Hub.dispatch("auth", { event: "signedOut" });
}

// ------------------------------------------------------------------ sign-up (patients only)
// Doctor, clinic, referee and admin logins are created by the API, so these
// always use the patient pool unless told otherwise.

export async function signUp({ username, password, options = {} }) {
  const attributes = options.userAttributes || {};
  return post("signup", {
    pool: options.pool || PATIENT_POOL,
    username,
    password,
    email: attributes.email || null,
    given_name: attributes.given_name || null,
    family_name: attributes.family_name || null,
  });
}

export async function confirmSignUp({ username, confirmationCode, options = {} }) {
  return post("signup/confirm", { pool: options.pool || PATIENT_POOL, username, code: String(confirmationCode || "") });
}

export async function resendSignUpCode({ username, options = {} }) {
  const data = await post("signup/resend", { pool: options.pool || PATIENT_POOL, username });
  return data.codeDeliveryDetails;
}

// ------------------------------------------------------------------ passwords

export async function resetPassword({ username, options = {} }) {
  return post("password/forgot", { pool: options.pool || getUserPool(), username });
}

export async function confirmResetPassword({ username, confirmationCode, newPassword, options = {} }) {
  await post("password/confirm", {
    pool: options.pool || getUserPool(),
    username,
    code: String(confirmationCode || ""),
    newPassword,
  });
}

export async function updatePassword({ oldPassword, newPassword }) {
  const { tokens } = await fetchAuthSession();
  if (!tokens) throw userUnauthenticated();
  await post("password/change", { oldPassword, newPassword });
}

// ------------------------------------------------------------------ axios

/**
 * On a 401 from the API, refresh the access token once and retry the
 * request; if that fails the session is gone and onSessionExpired runs.
 */
export function installAuthInterceptor(onSessionExpired) {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config;
      const isAuthCall = config && /(^|\/)auth\//.test(String(config.url || ""));
      if (!error.response || error.response.status !== 401 || !config || config._authRetried || isAuthCall)
        return Promise.reject(error);
      config._authRetried = true;
      const { tokens } = await fetchAuthSession({ forceRefresh: true });
      if (tokens) {
        config.headers = { ...config.headers, Authorization: `Bearer ${tokens.accessToken}` };
        return axios(config);
      }
      if (onSessionExpired) onSessionExpired();
      return Promise.reject(error);
    }
  );
}

// Restore the signed-in state on page load.
writeSession(readSession());
